// ═══════════════════════════════════════════════════════════════════
// ieBackdrop.js — THE BACKDROP BAND, React side (hooks over ieBackdropCore.js)
// MASTER COPY: gamemaster/engine/ieBackdrop.js — vendored byte-identical,
// always together with ieBackdropCore.js. The face is MenuBackdrop.js.
// ───────────────────────────────────────────────────────────────────
// Two vocabularies, one store:
//  • TERROR'S RAIL (railDock.js, kept so Terror can sit on this unchanged):
//      rail.toggle/open/close/collapse/expand/setPill/setConsoleTop,
//      useRail(), useRailHost(enabled, slotTop, slotBottom), railPanelStyle()
//  • THE EMPIRE'S ROWS (what each piece of chrome puts in the band):
//      useBackdropItems([{ id, section, label, icon?, run? | choices?, badge?, order? }])
//        section "play" | "people" | "settings" | "session" (foot) | your own
//        a row closes the menu, then runs; a choices row runs in place
//      useBackdropChips([{ id, label, value, icon?, tint? }])   the header
//      useBackdropTabs([{ id, label, icon?, badge?, order? }])  band PANELS —
//        the tab row shows them; the owner draws its own body into
//        railPanelStyle() while useRail().panel === id
//      useBackdropActive()  a band (or its phone sheet) is live on this screen:
//        chrome with a row/tab hides its own corner button only while true,
//        so a title with no band keeps its old behaviour (THE EXCEPTION LAW)
//      useBackdropOpen()    the phone SHEET is open (pause movement on it);
//        on desktop the band is just there, so this stays false
//      openBackdrop() / closeBackdrop() / toggleBackdrop() — the "menu" panel
//      setBackdropHidden(bool, reason) — reasons stack (login, indoors)
//      useRail().quiet — a title modal is up: a tab owner skips its docked body
// ═══════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { band, bandView, panelBox, newOwnerKey, BAND, bandWidthPx } from "./ieBackdropCore";

export { band, BAND, bandWidthPx } from "./ieBackdropCore";

const snap = () => band.get();
export function useBand() {
  const s = useSyncExternalStore(band.subscribe, snap, snap);
  return bandView(s);
}

// ── Terror's rail API ───────────────────────────────────────────────
export const rail = {
  toggle: band.toggle, open: band.open, close: band.close,
  collapse: band.collapse, expand: band.expand,
  setPill: band.setPill, setConsoleTop: band.setConsoleTop,
};
export function useRail() {
  const v = useBand();
  return { ...rail, ...v };
}
export function useRailHost(enabled = true, slotTop = BAND.SLOT_TOP, slotBottom = BAND.SLOT_BOTTOM) {
  useEffect(() => (enabled ? band.addHost(slotTop, slotBottom) : undefined), [enabled, slotTop, slotBottom]);
}
export const railPanelStyle = (slotTop, slotBottom, tab) => panelBox(slotTop, slotBottom, tab);

/** The px a title leaves free on the right for the band (0 when folded, on a
 *  phone, or with no band). Follows the window. Feed it to the canvas/viewport. */
export function useBandReserve() {
  const v = useBand();
  const [, tick] = useState(0);
  useEffect(() => {
    const on = () => tick((n) => n + 1);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return v.active && !v.collapsed ? bandWidthPx() : 0;
}

// ── The Empire's rows ───────────────────────────────────────────────
export const openBackdrop = () => band.open("menu");
export const closeBackdrop = () => band.close("menu");
export const toggleBackdrop = () => band.toggle("menu");
export const setBackdropHidden = (v, reason) => band.setHidden(v, reason);

// "A SHARED band is up here": live AND drawn by <MenuBackdrop/>. Terror's band is live
// but drawn by its own pills, so shared chrome (Friends' tab, Aemilia's nook…) stays out.
export function useBackdropActive() { const v = useBand(); return v.live && v.faced; }
export function useBackdropOpen() { const v = useBand(); return v.sheet && v.panel === "menu" && !v.collapsed; }

// Publish under this component's own key, keep functions behind a ref, and
// re-publish only when the SHAPE changes (ids, labels, badges, active flags).
function usePublish(kind, list, shapeOf) {
  const keyRef = useRef(null);
  if (keyRef.current === null) keyRef.current = newOwnerKey();
  const ref = useRef(list);
  ref.current = list;
  const shape = JSON.stringify((list || []).map(shapeOf));
  useEffect(() => {
    const key = keyRef.current;
    const live = (ref.current || []).map((it, i) => ({
      ...it,
      run: it.run ? (...a) => { const cur = ref.current && ref.current[i]; if (cur && typeof cur.run === "function") cur.run(...a); } : undefined,
      choices: it.choices ? it.choices.map((c, j) => ({
        ...c,
        run: () => { const cur = ref.current && ref.current[i] && ref.current[i].choices && ref.current[i].choices[j]; if (cur && typeof cur.run === "function") cur.run(); },
      })) : undefined,
    }));
    band.publish(kind, key, live);
    return () => band.unpublish(kind, key);
  }, [kind, shape]);
}
export function useBackdropItems(items) {
  usePublish("items", items, (it) => [it.id, it.section, it.label, it.icon, it.badge || 0, !!it.run, it.order,
    (it.choices || []).map((c) => [c.id, c.label, !!c.active])]);
}
export function useBackdropChips(chips) {
  usePublish("chips", chips, (c) => [c.id, c.label, String(c.value), c.icon, c.tint]);
}
export function useBackdropTabs(tabs) {
  usePublish("tabs", tabs, (t) => [t.id, t.label, t.icon, t.badge || 0, t.order]);
}
